import { useEffect, useRef } from 'react';
import { DexServiceConfig, LogData, SwapParams } from '../types/dex';
import { WorkerPoolService } from '../services/WorkerPoolService';

// 定义worker响应结果接口
interface WorkerResponse {
  workerId: number;
  success: boolean;
  data?: any;
  error?: string;
}

interface UseDexWorkerProps {
  config: Omit<DexServiceConfig, 'onLog'>;
  maxWorkers?: 1;
  onLog: (log: LogData) => void;
}

let workerPoolRef: WorkerPoolService | null = null;

export const useDexWorker = ({ config, maxWorkers = 1, onLog }: UseDexWorkerProps) => {

  useEffect(() => {
    // 创建WorkerPoolService实例
    if (!workerPoolRef) {
      // @ts-ignore
      window.workerPoolRef = workerPoolRef = new WorkerPoolService(config, 1, onLog);
    }

    // 清理函数
    return () => {
      if (workerPoolRef) {
        workerPoolRef.terminate();
        workerPoolRef = null;
      }
    };
  }, [config, maxWorkers, onLog]);

  return {
    swap: async (walletIndex: number, params: SwapParams) => {
      if (!workerPoolRef) {
        throw new Error('Worker pool not initialized');
      }
      return workerPoolRef.swap(walletIndex, params);
    },
    batchSwap: async (params: SwapParams[]) => {
      if (!workerPoolRef) {
        throw new Error('Worker pool not initialized');
      }
      // 确认Worker配置
      console.log('Worker配置:', workerPoolRef.getWorkerCount());
      return workerPoolRef.batchSwap(params);
    },
    getQuote: async (params: SwapParams) => {
      if (!workerPoolRef) {
        throw new Error('Worker pool not initialized');
      }
      // 获取所有worker的报价，并返回第一个成功的结果
      const responses = await workerPoolRef.getQuote(params);
      const successResponse = responses.find(r => r.success);
      if (successResponse) {
        return successResponse.data;
      }
      // 如果没有成功的响应，抛出第一个错误
      const firstError = responses[0]?.error || '获取报价失败';
      throw new Error(firstError);
    },
    setMaxWorkers: async (count: number) => {
      if (workerPoolRef) {
        await workerPoolRef.setMaxWorkers(count);
      }
    },
    terminate: async () => {
      if (workerPoolRef) {
        // 先终止当前实例
        workerPoolRef.terminate();
        workerPoolRef = null;
        
        // 使用当前作用域中的配置参数重新初始化实例
        workerPoolRef = new WorkerPoolService(
          config, 
          1, 
          onLog
        );
        
        console.log('已终止旧实例并自动初始化新实例');
      }
    },
    createWorker: async () => {
      if (!workerPoolRef) {
          workerPoolRef = await new WorkerPoolService(config, 1, onLog);
      }
      return workerPoolRef;
    },
    getWorkerCount: () => workerPoolRef?.getWorkerCount() ?? 0,
    getBusyWorkerCount: () => workerPoolRef?.getBusyWorkerCount() ?? 0,
    getActiveWorkerCount: () => workerPoolRef?.getActiveWorkerCount() ?? 0,
    getWorkerStatus: () => workerPoolRef?.getWorkerStatus() ?? []
  };
};
